import React, { useMemo, useState } from "react";
import GoogleSearchActions, {
  buildGoogleSearchUrl,
  openGoogleSearchPopup,
  trackGoogleSearchAction,
} from "./GoogleSearchActions";

export function buildConfirmedProductDescription(query, research, override = "") {
  const manual = String(override || "").replace(/\s+/g, " ").trim();
  if (manual) return manual.slice(0, 1800);
  const facts = Array.isArray(research?.facts)
    ? research.facts.map((f) => `${f.key || "상품 사실"}: ${String(f.value ?? "")}`)
    : [];
  const parts = [research?.identity, research?.summary, ...facts]
    .map((v) => String(v || "").replace(/\s+/g, " ").trim())
    .filter(Boolean);
  if (!parts.length) parts.push(String(query || "").trim());
  return [...new Set(parts)].join(". ").slice(0, 1800);
}

function host(url) {
  try { return new URL(url).hostname.replace(/^www\./, ""); }
  catch { return "외부 출처"; }
}

export default function ProductIdentityCard({ query, research, busy, onConfirm }) {
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correction, setCorrection] = useState("");
  const sources = Array.isArray(research?.sources) ? research.sources : [];
  // Arbitrary grounded pages can expose a flag, logo or banner as og:image. Only
  // curated product profiles are safe enough to show inline; all other products
  // use the explicit Google Images popup where the user can verify relevance.
  const images = useMemo(
    () => research?.profile_id && Array.isArray(research?.images) ? research.images.slice(0, 4) : [],
    [research],
  );
  const googleImageUrl = buildGoogleSearchUrl(query, "image");
  const confirmed = useMemo(
    () => buildConfirmedProductDescription(query, research),
    [query, research],
  );

  const submitCorrection = () => {
    const value = buildConfirmedProductDescription(query, research, correction);
    if (value) onConfirm?.(value);
  };

  return (
    <article style={{ border: "1px solid #cbd5e1", borderRadius: 10, background: "#fff", overflow: "hidden", boxShadow: "0 2px 8px rgba(15,23,42,.06)", margin: "10px 0 16px" }}>
      <div style={{ padding: "12px 16px", background: "#eff6ff", borderBottom: "1px solid #bfdbfe", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div>
          <strong style={{ color: "#1e3a5f", fontSize: 14 }}>먼저 상품을 확인해 주세요</strong>
          <div style={{ color: "#64748b", fontSize: 11.5, marginTop: 3 }}>웹 상품 식별 근거 · 아직 HS 분류 결과가 아닙니다</div>
        </div>
        <GoogleSearchActions
          query={query}
          compact
          modes={["product", "image"]}
          eventContext="product_identity_card"
        />
      </div>

      <div style={{ padding: "18px 18px 16px" }}>
        <div style={{ color: "#64748b", fontSize: 11.5 }}>검색한 상품명</div>
        <h2 style={{ margin: "4px 0 12px", fontSize: 19, color: "#111827" }}>{research?.identity || query}</h2>
        <div style={{ padding: "12px 13px", borderRadius: 8, background: "#f8fafc", color: "#334155", fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
          {research?.summary || "확인 가능한 상품 설명을 찾지 못했습니다."}
        </div>
        {research?.caution && <div style={{ marginTop: 8, color: "var(--audit-text)", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 7, padding: "8px 10px", fontSize: 11.5, lineHeight: 1.55 }}>{research.caution}</div>}

        {images.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ color: "#475569", fontSize: 11.5, fontWeight: 700, marginBottom: 6 }}>출처에서 찾은 상품 이미지</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
            {images.map((item, i) => (
              <a key={`${item.image_url || item.thumbnail_url}-${i}`} href={item.context_url || item.image_url} target="_blank" rel="noreferrer" title={item.title || "상품 이미지"} style={{ display: "block", height: 105, border: "1px solid #e2e8f0", borderRadius: 7, overflow: "hidden", background: "#f8fafc" }}>
                <img src={item.thumbnail_url || item.image_url} alt={item.title || "검색된 상품"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </a>
            ))}
            </div>
          </div>
        )}
        {images.length === 0 && (
          <div style={{ marginTop: 12, border: "1px dashed #cbd5e1", borderRadius: 8, background: "#f8fafc", padding: "11px 12px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <strong style={{ color: "#475569", fontSize: 12 }}>Google 이미지에서 실제 상품을 확인해 주세요.</strong>
              <div style={{ color: "#64748b", fontSize: 11.5, marginTop: 3 }}>출처 페이지의 국기·로고·배너를 상품 사진으로 오인하지 않도록 미확인 대표 이미지는 표시하지 않습니다.</div>
            </div>
            <a href={googleImageUrl} target="_blank" rel="noreferrer" onClick={(event) => { trackGoogleSearchAction("image", "product_identity_empty_image"); if (openGoogleSearchPopup(query, "image")) event.preventDefault(); }} style={{ border: "1px solid #cbd5e1", borderRadius: 7, background: "#fff", color: "#1d4ed8", padding: "7px 10px", fontSize: 11.5, fontWeight: 700, textDecoration: "none" }}>이미지로 외형 확인 ↗</a>
          </div>
        )}

        <div style={{ marginTop: 15 }}>
          <div style={{ fontSize: 12, fontWeight: 750, color: "#334155", marginBottom: 6 }}>확인 출처</div>
          {sources.length ? sources.slice(0, 5).map((source, i) => (
            <a key={`${source.url}-${i}`} href={source.url} target="_blank" rel="noreferrer" style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "8px 0", borderTop: i ? "1px solid #edf2f7" : 0, color: "#1d4ed8", textDecoration: "none", fontSize: 12.5 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{source.title || host(source.url)}</span>
              <span style={{ color: "#64748b", whiteSpace: "nowrap" }}>{host(source.url)} ↗</span>
            </a>
          )) : <div style={{ color: "var(--audit-text-muted)", fontSize: 12.5 }}>검색 출처가 함께 제공되지 않았습니다. 위 Google 링크에서 실제 상품을 확인한 뒤 선택해 주세요.</div>}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
          <button type="button" disabled={busy || !confirmed} onClick={() => onConfirm?.(confirmed)} style={{ border: 0, borderRadius: 8, background: "#2563eb", color: "#fff", padding: "9px 14px", fontWeight: 750, cursor: busy ? "wait" : "pointer" }}>이 제품이 맞습니다</button>
          <button type="button" disabled={busy} onClick={() => setCorrectionOpen((v) => !v)} style={{ border: "1px solid #cbd5e1", borderRadius: 8, background: "#fff", color: "#334155", padding: "9px 14px", cursor: "pointer" }}>다른 제품입니다</button>
        </div>

        {correctionOpen && (
          <div style={{ marginTop: 12, padding: 12, border: "1px solid #dbe3ec", borderRadius: 8, background: "#f8fafc" }}>
            <label htmlFor="product-correction" style={{ display: "block", color: "#475569", fontSize: 12, marginBottom: 6 }}>제품의 용도·형태를 알고 있는 만큼 입력해 주세요.</label>
            <div style={{ display: "flex", gap: 7 }}>
              <input id="product-correction" value={correction} onChange={(e) => setCorrection(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) submitCorrection(); }} placeholder="예: 피부에 바르는 미백 화장품 앰플, 액상 5ml × 5개" style={{ flex: 1, minWidth: 0, border: "1px solid #cbd5e1", borderRadius: 7, padding: "9px 10px", fontFamily: "inherit" }} />
              <button type="button" disabled={busy || !correction.trim()} onClick={submitCorrection} style={{ border: 0, borderRadius: 7, background: "#334155", color: "#fff", padding: "0 12px", cursor: "pointer" }}>이 설명으로 분류</button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
